/**
 * Login Support Page (Public, No Auth)
 *
 * For users who can't log in - simple form to submit a support ticket.
 * Route: /support (public)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LifeBuoy, Send, CheckCircle2, ArrowLeft, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCreateLoginTicket } from '@/hooks/useSupport';
import type { TicketCategory } from '@/types/database';

const LOGIN_CATEGORIES: { value: TicketCategory; key: string }[] = [
  { value: 'account', key: 'account' },
  { value: 'bug', key: 'bug' },
  { value: 'other', key: 'other' },
];

export function LoginSupportPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.language?.substring(0, 2) || 'hr';
  const createTicket = useCreateLoginTicket();

  const [email, setEmail] = useState('');
  const [category, setCategory] = useState<TicketCategory>('account');
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const toggleLanguage = () => {
    i18n.changeLanguage(language === 'hr' ? 'en' : 'hr');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !description.trim()) return;

    await createTicket.mutateAsync({
      email: email.trim(),
      category,
      subject: t(`support.loginForm.categories.${category}`),
      description: description.trim(),
    });

    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      {/* Language Toggle */}
      <button
        onClick={toggleLanguage}
        className="fixed top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 text-white/80 hover:bg-white/20 text-sm transition-colors"
      >
        <Globe className="w-4 h-4" />
        {language === 'hr' ? 'EN' : 'HR'}
      </button>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img src="/favicon-original.png" alt="BookiApp" className="h-12 w-auto mx-auto mb-3" />
          </Link>
        </div>

        {submitted ? (
          /* Success State */
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/10 p-8 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20 mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              {t('support.loginForm.successTitle')}
            </h2>
            <p className="text-sm text-white/60 mb-6">
              {t('support.loginForm.successMessage')}
            </p>
            <p className="text-xs text-white/40 mb-6">
              {t('support.loginForm.contactFallback')}{' '}
              <a href="mailto:admin@bookiapp.com" className="text-amber-400 hover:text-amber-300">
                admin@bookiapp.com
              </a>
            </p>
            <Link to="/">
              <Button variant="outline" className="gap-2 border-white/20 text-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4" />
                {t('support.loginForm.backToLogin')}
              </Button>
            </Link>
          </div>
        ) : (
          /* Form */
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/10 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-500">
                <LifeBuoy className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">
                  {t('support.loginForm.title')}
                </h1>
                <p className="text-sm text-white/60">
                  {t('support.loginForm.subtitle')}
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="text-sm font-medium text-white/80 mb-1.5 block">
                  {t('support.loginForm.email')}
                </label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('support.loginForm.emailPlaceholder')}
                  className="bg-white/10 border-white/10 text-white placeholder:text-white/30 focus:border-amber-500"
                />
              </div>

              {/* Category */}
              <div>
                <label className="text-sm font-medium text-white/80 mb-1.5 block">
                  {t('support.loginForm.whatHappens')}
                </label>
                <div className="flex gap-2">
                  {LOGIN_CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setCategory(cat.value)}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        category === cat.value
                          ? 'bg-amber-500 text-white'
                          : 'bg-white/10 text-white/60 hover:bg-white/20'
                      }`}
                    >
                      {t(`support.loginForm.categories.${cat.key}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-white/80 mb-1.5 block">
                  {t('support.loginForm.describeIssue')}
                </label>
                <textarea
                  required
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('support.descriptionPlaceholder')}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg text-sm bg-white/10 border border-white/10 text-white placeholder:text-white/30 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={!email.trim() || !description.trim() || createTicket.isPending}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 gap-2"
              >
                {createTicket.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('support.loginForm.sending')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t('support.loginForm.submit')}
                  </>
                )}
              </Button>
            </form>

            {/* Fallback */}
            <p className="text-xs text-white/40 text-center mt-4">
              {t('support.loginForm.contactFallback')}{' '}
              <a href="mailto:admin@bookiapp.com" className="text-amber-400 hover:text-amber-300">
                admin@bookiapp.com
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginSupportPage;
